import { useEffect, useRef, useState } from 'react';
// La config de ESLint no reconoce el uso de `motion.div` en JSX; el import sí se usa.
import { motion } from 'motion/react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import blackArrow from "../images/black-arrow.svg";
import whiteArrow from "../images/white-arrow.svg";
import { ImageWrapper } from './ImageWrapper.jsx';
import { fs } from '../utils/a11y.js';
import { checkoutTexts } from '../i18n/checkout.es.js';
import {
  PAYMENT_WAIT_DEFAULTS,
  PAYMENT_WARNING_SECONDS,
  createCountdown,
  getAnnouncedSeconds,
} from '../utils/paymentWait.js';

const texts = checkoutTexts.paymentWait;

// Nombre visible de cada POS; type_details trae el identificador interno.
const POS_BRAND_NAMES = {
  Getnet: 'Getnet',
  MercadoPago: 'Mercado Pago',
  Transbank: 'Transbank',
};

// Dónde va la flecha y hacia dónde apunta, según en qué parte del tótem está el POS. La imagen de
// la flecha apunta a la derecha, así que el giro se mide desde ahí. 'right' es el comportamiento
// que había antes de KIO-01.
const ARROW_PLACEMENTS = {
  'right':        { className: 'bottom-[15%] right-10', rotate: 0,   animate: { x: [0, 30, 0] } },
  'left':         { className: 'bottom-[15%] left-10',  rotate: 180, animate: { x: [0, -30, 0] } },
  'bottom':       { className: 'bottom-10 left-[calc(50%-50px)]', rotate: 90, animate: { y: [0, 30, 0] } },
  'bottom-right': { className: 'bottom-10 right-10',    rotate: 45,  animate: { x: [0, 20, 0], y: [0, 20, 0] } },
  'bottom-left':  { className: 'bottom-10 left-10',     rotate: 135, animate: { x: [0, -20, 0], y: [0, 20, 0] } },
};

// Colores con contraste >= 4,5:1 sobre el fondo del modal (--midground) en cada tema. El gris
// #6a7282 del resto del modal no llega a 4,5:1 en oscuro, por eso aquí no se usa.
const THEME_COLORS = {
  light: { secondary: '#4B5563', warning: '#B42318', track: 'rgba(10, 10, 10, 0.12)' },
  dark:  { secondary: '#D1D5DB', warning: '#FDA29B', track: 'rgba(250, 250, 250, 0.18)' },
};

/**
 * KIO-01: pantalla mientras el cliente paga en el POS (estados `creating` y `at_terminal`).
 *
 * Dice qué hacer, cuánto tiempo queda y suena al empezar y cuando quedan 15 s. La cuenta regresiva
 * es solo visual: al llegar a 0 llama a onTimeout, pero no corta el cobro (eso es de KIO-03).
 *
 * @param {object} props
 * @param {string} props.amount - Monto ya formateado.
 * @param {string|null} props.brand - type_details del medio de pago (Getnet, MercadoPago, Transbank).
 * @param {object} props.config - Resultado de getPaymentWaitConfig(style).
 * @param {{current: HTMLAudioElement|null}} props.audioRef - Audio precargado por el modal.
 * @param {'light'|'dark'} props.theme
 * @param {Function} [props.logActivity]
 * @param {object} [props.activityMetadata] - Se agrega a los registros de actividad.
 * @param {() => void} [props.onTimeout]
 */
export function PaymentWaitScreen({ amount, brand, config, audioRef, theme, logActivity, activityMetadata, onTimeout }) {
  const { posSide, paymentTimeoutSeconds, paymentSound, paymentSoundVolume } = config || PAYMENT_WAIT_DEFAULTS;
  const [remaining, setRemaining] = useState(paymentTimeoutSeconds);

  // Las funciones del padre cambian en cada render; se leen desde refs para que la cuenta regresiva
  // no se reinicie cada vez que el modal se vuelve a pintar.
  const callbacksRef = useRef({});
  callbacksRef.current = { logActivity, activityMetadata, onTimeout, paymentSound, paymentSoundVolume, audioRef };

  useEffect(() => {
    // El navegador puede bloquear el audio sin interacción previa (autoplay); en ese caso se sigue
    // sin sonido y sin ensuciar la consola.
    const playSound = () => {
      const { paymentSound: enabled, paymentSoundVolume: volume, audioRef: ref } = callbacksRef.current;
      const audio = ref?.current;
      if (!enabled || !audio) return;
      try {
        audio.volume = volume;
        audio.currentTime = 0;
        const playing = audio.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      } catch {
        // Sin sonido.
      }
    };

    const log = (action, extra) => {
      const { logActivity: _log, activityMetadata: metadata } = callbacksRef.current;
      _log?.('checkout-modal', 'payment-wait', action, false, { ...metadata, pos_side: posSide, timeout_seconds: paymentTimeoutSeconds, ...extra });
    };

    log('shown');
    playSound();

    const countdown = createCountdown({
      totalSeconds: paymentTimeoutSeconds,
      onTick: setRemaining,
      onWarning: playSound,
      onTimeout: () => {
        log('timeout-visual');
        callbacksRef.current.onTimeout?.();
      },
    });
    countdown.start();
    return countdown.stop;
  }, [paymentTimeoutSeconds, posSide]);

  const colors = THEME_COLORS[theme === 'dark' ? 'dark' : 'light'];
  const isWarning = remaining <= PAYMENT_WARNING_SECONDS;
  const timeColor = isWarning ? colors.warning : 'var(--color-text)';
  const progress = paymentTimeoutSeconds > 0 ? (remaining / paymentTimeoutSeconds) * 100 : 0;
  const announced = getAnnouncedSeconds(remaining);
  const brandName = brand ? (POS_BRAND_NAMES[brand] || brand) : null;
  const arrow = ARROW_PLACEMENTS[posSide] || ARROW_PLACEMENTS[PAYMENT_WAIT_DEFAULTS.posSide];

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="text-center w-full max-w-[760px]">
        {/* Hasta 600 px, pero nunca más del 35 % del alto: en HD vertical (1280 px) a tamaño completo
            empuja el texto y el temporizador hasta la flecha. En Full HD y QHD queda igual. */}
        <div className="a11y-checkout-anim w-150 h-150 max-w-[35vh] max-h-[35vh] my-0 mx-auto">
          <DotLottieReact
            src="lotties/contactlessPosAnimation.lottie"
            themeId={brand}
            loop
            autoplay
          />
        </div>
        <h2 className="main-font-bold mb-4" style={{ color: 'var(--color-text)', fontSize: fs(44), lineHeight: fs(52) }}>
          {texts.title}
        </h2>
        <p className="secondary-font-regular" style={{ color: colors.secondary, fontSize: fs(24), lineHeight: fs(32) }}>
          {texts.amountLabel}{' '}
          <span className="secondary-font-bold" style={{ color: 'var(--color-text)' }}>{amount}</span>
          {brandName ? <span>{' · '}{texts.posLabel} {brandName}</span> : null}
        </p>

        <div className="mt-10" aria-hidden="true">
          <div className="secondary-font-regular" style={{ color: colors.secondary, fontSize: fs(18), lineHeight: fs(24) }}>
            {texts.timeLeftLabel}
          </div>
          <div className="main-font-bold tabular-nums" style={{ color: timeColor, fontSize: fs(64), lineHeight: fs(72) }}>
            {remaining} {texts.secondsUnit}
          </div>
          {/* Angosta y centrada bajo el número, con margen amplio a los lados: así se lee como parte
              del temporizador y no choca con la flecha, que va pegada a un borde de la pantalla. */}
          <div className="w-[280px] max-w-full mx-auto rounded-full overflow-hidden mt-3" style={{ height: '16px', backgroundColor: colors.track }}>
            <div
              className="h-full rounded-full"
              style={{
                width: `${progress}%`,
                backgroundColor: timeColor,
                transition: 'width 1s linear, background-color 0.3s ease',
              }}
            />
          </div>
        </div>
        <p className="sr-only" aria-live="polite">
          {announced > 0 ? texts.timeLeftAnnouncement(announced) : texts.timeUpAnnouncement}
        </p>

        <motion.div
          className={`w-[100px] h-[100px] absolute ${arrow.className}`}
          style={{ rotate: arrow.rotate }}
          animate={arrow.animate}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ImageWrapper
            image={theme === 'dark' ? whiteArrow : blackArrow}
            alt={texts.arrowAlt}
            display="block"
            width="100%"
            height="100%"
            backgroundColor="transparent"
            margin="0"
            padding="0"
            borderRadius="0"
            transition={true}
            loadDelay={0.1}
            onLoad={undefined}
          />
        </motion.div>
      </div>
    </div>
  );
}
